/*
    Displays all the members within the organisation
    Admin users can disable (remove) non-admin members from the Cognito group
*/

import React, { useEffect, useState } from 'react';
import { listTeamMembers, removeTeamMember } from '../utils/apiClient';
import Navbar from '../components/Navbar';
import { CircularProgress, Container, Typography, List, ListItem, ListItemAvatar, ListItemText, Avatar, Paper, Box, IconButton, ThemeProvider, createTheme, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { Person, Delete } from '@mui/icons-material';

const darkTheme = createTheme({
    palette: {
        mode: 'dark', 
        background: {
            default: '#121212', 
            paper: '#1e1e1e',
        },
        text: {
            primary: '#ffffff',
            secondary: '#bbbbbb', 
        },
    },
});

function Team() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null); // user object pending confirmation
    const [deleting, setDeleting] = useState(false);

    const storedUserData = JSON.parse(localStorage.getItem('userData') || '{}');

    useEffect(() => {
        if (storedUserData) {
            setIsAdmin(storedUserData['custom:isAdmin'] === "True"); 
        }

        const fetchUsersInGroup = async () => {
            try {
                const users = await listTeamMembers();
                setUsers(users);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching users from group:', error);
                setLoading(false);
            }
        };

        fetchUsersInGroup();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await removeTeamMember(deleteTarget.cognitoUsername);
            // Remove from local state
            setUsers(prev => prev.filter(u => u.cognitoUsername !== deleteTarget.cognitoUsername));
        } catch (err) {
            console.error('Error removing user:', err);
        } finally {
            setDeleting(false);
            setDeleteTarget(null);
        }
    };

    return (
        <div>
            <Navbar />
            <ThemeProvider theme={darkTheme}>
                <Container maxWidth="md">
                    <Paper elevation={3} sx={{ padding: 4, marginTop: 4, backgroundColor: 'background.paper' }}>
                        <Typography variant="h4" gutterBottom color="textPrimary">
                            Team Members
                        </Typography>
                        {loading ? (
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                                <CircularProgress color="inherit" />
                            </Box>
                        ) : (
                            <List>
                                {users.map(user => (
                                    <ListItem
                                        key={user.cognitoUsername}
                                        secondaryAction={
                                            isAdmin && !user.isUserAdmin ? (
                                                <IconButton edge="end" aria-label="delete" onClick={() => setDeleteTarget(user)}>
                                                    <Delete sx={{ color: 'red' }} />
                                                </IconButton>
                                            ) : null
                                        }
                                    >
                                        <ListItemAvatar>
                                            <Avatar sx={{ color: 'white' }}>
                                                <Person />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={user.displayName}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Paper>
                </Container>
            </ThemeProvider>

            {/* Confirmation dialog for user removal */}
            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
                <DialogTitle>Remove Team Member</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to remove <strong>{deleteTarget?.displayName}</strong> from the team?
                        They will be removed from the group and their account will be disabled.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
                        {deleting ? 'Removing...' : 'Remove'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default Team;
